<template>
  <div class="md:flex md:justify-center">
    <div class="md:overflow-auto flex pt-8 md:pt-16">
      <transition :name="cardToShowTiers ? 'slide-left' : 'slide-right'" mode="out-in" @afterEnter="enterOverflow" @leave="leaveOverflow">
        <TierGroup
          v-if="cardToShowTiers"
          :answer="cardToShowTiers"
          :selectedTier="getSelectedTier(cardToShowTiers.uid)"
          @click:tier-option="handleTierSelect"
          @click:selection-card="handleCardSelect"
          @click:close-tier-group="() => { cardToShowTiers = null }"
        />
        <div
          v-else
          class="selection-group__grid grid grid-cols-2 md:flex w-full"
          role="listbox"
          aria-orientation="horizontal"
          :aria-multiselectable="multiple"
          v-bind="$attrs"
        >
          <template v-for="(option, index) in options" :key="option.uid">
            <SelectionCard
              :is-selected="isCardSelected(option)"
              :value="option"
              :ref="(el) => mapSelectionCardRefs(el, index)"
              @select="handleCardSelect"
            >
              <slot name="card" :value="option">
                <SelectionCardLayout :title="option.heading" :body="option.description" :image="option.image" />
              </slot>
            </SelectionCard>
            <div :style="moreInfoStyle(index)">
              <transition
                :name="index === options.length - 1 ? 'reveal-last' : 'reveal'"
              >
                <MoreInfo
                  v-if="cardToShowMoreInfo === option"
                  class="my-1 md:my-0"
                  :class="{
                    'md:ml-4':
                      cardToShowMoreInfo === option && index === options.length - 1,
                  }"
                  @close="handleMoreInfoClose"
                >
                  <slot name="more-info" :value="cardToShowMoreInfo">
                    <div class="text-left" v-dompurify-html="addAriaToLinks(cardToShowMoreInfo.moreInfo)" />
                    <PhotochromaticModal
                      v-if="cardToShowMoreInfo.hasPhotochromatic"
                      :photochromatic="cardToShowMoreInfo.photochromatic"/>
                  </slot>
                </MoreInfo>
              </transition>
            </div>
          </template>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, nextTick, watch } from 'vue'
import SelectionCard from '@/components/atoms/SelectionCard/index.vue'
import SelectionCardLayout from '@/components/molecules/SelectionCardLayout/index.vue'
import MoreInfo from '@/components/atoms/MoreInfo/index.vue'
import TierGroup from '@/components/molecules/TierGroup/index.vue'
import PhotochromaticModal from '@/components/molecules/PhotochromaticModal/index.vue'
import { Selection, Answer, Tier } from '@/model'

export default defineComponent({
  name: 'SelectionGroup',
  inheritAttrs: false,
  components: { SelectionCard, SelectionCardLayout, MoreInfo, TierGroup, PhotochromaticModal },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Array as () => Array<Selection>,
      required: true,
      validator: (value: Array<Selection>) =>
        value.every((i) => i instanceof Selection)
    },
    options: {
      type: Array as () => Array<Answer>,
      required: true,
      validator: (value: Array<Answer>) =>
        value.every((i) => i instanceof Answer)
    },
    multiple: {
      type: Boolean,
      default: false
    }
  },
  setup (props, { emit }) {
    const cardToShowMoreInfo = ref(null as Answer | null)
    const cardToShowMoreInfoOrder = ref(0)
    const cardToShowTiers = ref(null as Answer | null)
    const selectionCards = ref(
      [] as Array<InstanceType<typeof SelectionCard>>
    )

    watch(() => props.options, () => {
      cardToShowMoreInfo.value = null
      cardToShowMoreInfoOrder.value = 0
      cardToShowTiers.value = null
    })

    const mapSelectionCardRefs = (el: any, index: number) => {
      if (el) selectionCards.value[index] = el
    }

    const moreInfoStyle = (index: number) => {
      const rowStart = Math.floor(index / 2) * 2 + 2
      return {
        gridRowStart: rowStart,
        gridRowEnd: rowStart + 1,
        gridColumnStart: 1,
        gridColumnEnd: 3
      }
    }

    const updateModel = (option: Answer, isClickToSelect: boolean) => {
      let updatedModel
      if (isClickToSelect) {
        const newValue = option.hasTiers ? new Selection(option, option.tiers[0]) : new Selection(option)
        updatedModel = props.multiple && !option.isExclusive
          ? props.modelValue.filter(({ answer: { isExclusive } }) => !isExclusive).concat(newValue)
          : [newValue]
      } else {
        updatedModel = props.modelValue.filter(({ answer: { uid } }) => uid !== option.uid)
      }
      emit('update:modelValue', updatedModel)
    }

    const toggleMoreInfo = (option: Answer, isClickToSelect: boolean) => {
      cardToShowMoreInfo.value = null

      if (isClickToSelect && option.hasMoreInfo) {
        nextTick().then(() => {
          cardToShowMoreInfoOrder.value = props.options.findIndex(
            (o) => o === option
          )
          cardToShowMoreInfo.value = option
        })
      }
    }

    const toggleTier = (option: Answer, isClickToSelect: boolean) => {
      cardToShowTiers.value = null
      if (isClickToSelect && option.hasTiers) {
        nextTick().then(() => {
          cardToShowTiers.value = option
        })
      }
    }

    const handleCardSelect = (
      option: Answer,
      isClickToSelect: boolean
    ) => {
      updateModel(option, isClickToSelect)
      toggleMoreInfo(option, isClickToSelect)
      toggleTier(option, isClickToSelect)
    }

    const handleTierSelect = (tier: Tier, answer: Answer) => {
      const updatedSelection = new Selection(answer, tier)
      const updatedModel = props.modelValue.filter(({ answer: { uid } }) => answer.uid !== uid).concat(updatedSelection)
      emit('update:modelValue', updatedModel)
    }

    const handleMoreInfoClose = () => {
      (
        selectionCards.value?.[cardToShowMoreInfoOrder.value]
          .$el as HTMLElement
      ).focus()
      cardToShowMoreInfo.value = null
    }

    const isCardSelected = (option: Answer) => {
      return props.modelValue.some(({ answer: { uid } }) => option.uid === uid)
    }

    const getSelectedTier = (optionUid: string): Tier | undefined => {
      return props.modelValue.find(({ answer: { uid } }) => optionUid === uid)?.tier
    }

    const enterOverflow = (el: any) => {
      el.parentElement.classList.remove('md:overflow-hidden')
      el.parentElement.classList.add('md:overflow-auto')
    }

    const leaveOverflow = (el: any) => {
      el.parentElement.classList.remove('md:overflow-auto')
      el.parentElement.classList.add('md:overflow-hidden')
    }
    
    const addAriaToLinks = (html: string) => {
      if (!html) return ''

      html = html.replace(
        /<a\s+([^>]*href="([^"]+)"[^>]*)>(.*?)<\/a>/gi,
        (match, attrs, href, text) => {
          const cleanText = text.replace(/<[^>]+>/g, '').trim()
          let hostname = ''
          try {
            hostname = new URL(href).hostname.replace(/^www\./, '');
          } catch {
            hostname = 'external site'
          }
          const label = `${cleanText} ${hostname}`
          if (attrs.includes('aria-label')) return match
          const extra = attrs.includes('target="_blank"')
            ? ' (opens in a new window)'
            : ''
          return `<a ${attrs} aria-label="${label}${extra}">${text}</a>`
        }
      )

      return html
    }


    return {
      cardToShowMoreInfo,
      cardToShowTiers,
      mapSelectionCardRefs,
      moreInfoStyle,
      handleCardSelect,
      handleMoreInfoClose,
      isCardSelected,
      getSelectedTier,
      handleTierSelect,
      enterOverflow,
      leaveOverflow,
      addAriaToLinks
    }
  }
})
</script>

<style scoped>
::v-deep(.info-tip) {
  position: absolute;
  bottom: 100%;
}

.selection-group {
  &__grid {
    column-gap: theme("spacing.2");
    row-gap: theme("spacing.1");
  }
}

/* reveal transition */
.reveal-enter-active,
.reveal-last-enter-active,
.reveal-last-leave-active,
.reveal-leave-active {
  transition: clip-path 0.25s, margin-top 0.25s, opacity 0.2s;
  clip-path: inset(0);
  margin-top: theme("spacing.1");
  opacity: 1;
}

.reveal-enter-from,
.reveal-last-enter-from,
.reveal-last-leave-to,
.reveal-leave-to {
  clip-path: inset(100% 0 0 0);
  margin-top: -200px;
  opacity: 0;
}

@screen md {
  .selection-group {
    &__grid {
      column-gap: unset;
      row-gap: unset;
      & > * {
        margin-right: theme("spacing.4");

        &:nth-last-child(1),
        &:nth-last-child(2) {
          margin-right: 0;
        }
      }
    }
  }

  .reveal-enter-active,
  .reveal-leave-active,
  .reveal-last-enter-active,
  .reveal-last-leave-active {
    transition: clip-path 0.5s, margin 0.5s;
    clip-path: inset(0);
    margin-left: 0px;
    margin-top: unset;
  }

  .reveal-last-enter-active,
  .reveal-last-leave-active {
    margin-left: theme("spacing.4");
  }

  .reveal-enter-from,
  .reveal-last-enter-from,
  .reveal-leave-to,
  .reveal-last-leave-to {
    clip-path: inset(0 0 0 100%);
    margin-left: -340px;
    margin-top: unset;
    opacity: 1;
  }
}
</style>
